import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

const Header = () => {
  const navigate = useNavigate();
  const userName = sessionStorage.getItem("user_name");
  const [isPWA, setIsPWA] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Detect mobile device and web view
  useEffect(() => {
    const checkMobile = () => {
      // More accurate mobile detection - check both viewport and user agent
      const isMobileViewport = window.innerWidth <= 768;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // Only show mobile view if both conditions are true AND it's not a desktop browser
      const isDesktopBrowser = !isMobileUserAgent && window.innerWidth > 1024;
      const isMobile = isMobileViewport && isMobileUserAgent && !isDesktopBrowser;
      setIsMobile(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect PWA mode
  useEffect(() => {
    const checkPWAMode = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      const isInApp = window.matchMedia('(display-mode: fullscreen)').matches;
      const isPWA = isStandalone || isInApp;
      
      setIsPWA(isPWA);
      console.log('PWA Mode:', isPWA, 'Standalone:', isStandalone, 'InApp:', isInApp);
    };
    
    checkPWAMode();
  }, []);

  // PWA-specific features (only when in PWA mode)
  useEffect(() => {
    if (!isPWA) return;

    // Handle online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Handle PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
      console.log('PWA: Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);


    // Check theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
      document.body.classList.toggle('dark-theme', savedTheme === 'dark');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isPWA]);

  const logout = () => {
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  // PWA-specific functions
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  // Mobile tab navigation functions
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    if (isMobile) {
      setIsSidebarOpen(true);
    }
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Show mobile header only on mobile devices, keep original for web view
  if (isMobile) {
    return (
      <>
        {/* Mobile Header */}
        <nav className={`mobile-header ${isPWA ? 'pwa-header' : ''} ${isDarkMode ? 'dark-theme' : ''}`}>
          <div className="mobile-header-container">
            {/* Left side - Menu button and Logo */}
            <div className="mobile-header-left">
              <span className="mobile-logo">EXO</span>
            </div>

            {/* Center - User info with status */}
            <div className="mobile-header-center">
              <div className="mobile-user-info">
                <span className="mobile-user-name">{userName}</span>
                {isPWA && (
                  <div className="mobile-status-indicator">
                    <span 
                      className={`mobile-status-dot ${isOnline ? 'online' : 'offline'}`}
                      title={isOnline ? 'Online' : 'Offline'}
                    ></span>
                  </div>
                )}
              </div>
            </div>

            {/* Right side - PWA Controls */}
            <div className="mobile-header-right">
              <div className="mobile-controls">
                {/* PWA-only features */}
                {isPWA && (
                  <>
                    {/* PWA Install Button */}
                    {showInstallPrompt && (
                      <button 
                        className="mobile-control-btn install-btn" 
                        type="button" 
                        onClick={handleInstallApp}
                        title="Install App"
                      >
                        <i className="simple-icon-plus"></i>
                      </button>
                    )}

                  </>
                )}

                {/* Logout Button */}
                <button 
                  className="mobile-control-btn logout-btn" 
                  type="button" 
                  onClick={logout}
                  title="Logout"
                >
                  <i className="simple-icon-power"></i>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </>
    );
  }

  // Original web view header (unchanged)
  return (
    <nav className="navbar fixed-top">
        <div className="d-flex align-items-center navbar-left">
            <span style={{fontSize: 24, textTransform: 'uppercase', padding: '0px 30px', fontWeight: 500}}>Exo</span>
        </div>

        <Link to={'#'} className="navbar-logo" style={{fontWeight: 500, fontSize: 24}}>
          {userName}
        </Link>

        <div className="navbar-right">
            <div className="header-icons d-inline-block align-middle">
                <button className="header-icon btn btn-empty d-none d-sm-inline-block" type="button" onClick={logout}>
                    <i style={{fontSize: 24}} className="simple-icon-power"></i>
                </button>
            </div>
        </div>
    </nav>
  );
};

export default Header;
